<template>
  <div style="width: 97%">
    <div>
      <label>{{ $t("emploi.listeEnseignants") }}</label>
      <a-select
        id="selectClass"
        class="mb-3"
        show-search
        :placeholder="$t('emploi.listeEnseignants')"
        option-filter-prop="children"
        :filter-option="filterOption"
        style="width: 400px; margin-left: 15px"
        @change="
          (val) => {
            selectTeacher(val);
          }
        "
      >
        <a-select-option
          v-for="teacher in teachers"
          :key="teacher._id"
          :value="teacher._id"
        >
          {{ formatFirstMiddleLast(teacher.employee) }}
        </a-select-option>
      </a-select>
    </div>

    <a-divider class="mb-2 mt-0"></a-divider>
    <div
      v-if="currentUserId != '0'"
      style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px"
    >
      <div style="display: flex; align-items: center; gap: 10px">
        <span style="font-weight: 600">{{ $t("chat.filter") }}:</span>
        <a-radio-group v-model="roomFilter">
          <a-radio-button value="individual">{{
            $t("chat.individualChats")
          }}</a-radio-button>
          <a-radio-button value="group">{{ $t("chat.groupChats") }}</a-radio-button>
        </a-radio-group>
      </div>
    </div>

    <a-col class="mt-5" v-if="!loadedChat" :span="16" :offset="11">
      <a-spin size="large" />
    </a-col>
    <vue-advanced-chat
      v-if="currentUserId != '0' && loadedChat"
      height="calc(90vh - 120px)"
      class="mr-10"
      :current-user-id="currentUserId"
      :rooms="JSON.stringify(filteredRooms)"
      :text-messages="JSON.stringify(textMessages)"
      show-reaction-emojis="false"
      show-audio="false"
      show-add-room="false"
      :messages="JSON.stringify(messages)"
      :message-actions="JSON.stringify(messageActions)"
      :messages-loaded="messagesLoaded"
      :rooms-loaded="roomsLoaded"
      :room-info-enabled="
        !!selectedRoom && selectedRoom.roomType === 'parent-group-supervision'
      "
      @fetch-messages="fetchMessages($event.detail[0])"
      @open-file="openFile($event.detail[0])"
      @room-info="handleRoomInfo($event.detail[0])"
      load-first-room="true"
      show-new-messages-divider="false"
      show-footer="false"
    >
      <chat-message-video-slot
        v-for="msg in messagesWithChatVideo"
        :key="'pt-chat-vid-' + messageSlotKey(msg)"
        :slot="'message_' + messageSlotKey(msg)"
        :message="msg"
        :current-user-id="String(currentUserId)"
        :resources-url="settings.resourcesUrl"
        :room-users="videoSlotRoomUsers"
        @delete="handleVideoSlotDelete"
      />
    </vue-advanced-chat>

    <a-modal
      v-model="showGroupInfo"
      :title="$t('chat.groupDetails')"
      :footer="null"
      width="640px"
    >
      <div v-if="selectedRoom && selectedRoom.roomType === 'parent-group-supervision'">

        <!-- Header: avatar + name + mode badge -->
        <div style="display: flex; align-items: flex-start; gap: 16px; margin-bottom: 16px">
          <img
            :src="selectedRoom.avatar && selectedRoom.avatar !== 'media/avatars/blank.png'
              ? selectedRoom.avatar
              : getAvatarUrl(null, selectedRoom.groupName)"
            :alt="selectedRoom.groupName"
            style="width: 72px; height: 72px; border-radius: 12px; object-fit: cover; border: 2px solid #f0f0f0; flex-shrink: 0"
          />
          <div style="flex: 1; min-width: 0">
            <!-- Name + selection mode badge -->
            <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 6px">
              <span style="font-size: 18px; font-weight: 700; color: #111">
                {{ selectedRoom.groupName || selectedRoom.roomName }}
              </span>
              <a-tag
                :color="selectedRoom.selectionMode === 'manual' ? 'default' : 'orange'"
                style="font-size: 11px; font-weight: 600"
              >
                {{
                  selectedRoom.selectionMode === "subject"
                    ? $t("chat.subjectBased")
                    : selectedRoom.selectionMode === "classroom"
                    ? $t("chat.classroomBased")
                    : $t("chat.manualSelection")
                }}
              </a-tag>
            </div>

            <!-- Description -->
            <p
              style="font-size: 13px; margin-bottom: 8px; line-height: 1.5"
              :style="selectedRoom.description ? 'color: #374151' : 'color: #9ca3af; font-style: italic'"
            >
              {{ selectedRoom.description || $t("chat.noDescription") }}
            </p>

            <!-- Meta row: class, subject, teacher -->
            <div style="display: flex; flex-wrap: wrap; gap: 6px 20px">
              <span
                v-if="selectedRoom.classRoom"
                style="display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #6b7280"
              >
                <a-icon type="bank" style="color: #9ca3af" />
                <span style="font-weight: 500">{{ $t("chat.classLabel") }}:</span>
                <span style="font-weight: 600; color: #374151">
                  {{ selectedRoom.classRoom.name || selectedRoom.classRoom }}
                </span>
              </span>

              <!-- Subject: always shown for non-manual -->
              <span
                v-if="selectedRoom.selectionMode !== 'manual'"
                style="display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #6b7280"
              >
                <a-icon type="book" style="color: #9ca3af" />
                <span style="font-weight: 500">{{ $t("chat.subjectLabel") }}:</span>
                <span
                  :style="selectedRoom.subject
                    ? 'font-weight: 600; color: #374151'
                    : 'color: #9ca3af; font-style: italic'"
                >
                  {{ selectedRoom.subject
                    ? (selectedRoom.subject.name || selectedRoom.subject)
                    : $t("chat.noSubject") }}
                </span>
              </span>

              <!-- Teacher -->
              <span style="display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #6b7280">
                <img
                  :src="getAvatarUrl(selectedRoom.teacherPhoto, selectedRoom.teacherName)"
                  style="width: 20px; height: 20px; border-radius: 50%; object-fit: cover"
                />
                <span style="font-weight: 500">{{ $t("chat.teacher") }}:</span>
                <span style="font-weight: 600; color: #374151">
                  {{ selectedRoom.teacherName || $t("chat.teacher") }}
                </span>
              </span>
            </div>
          </div>
        </div>

        <a-divider style="margin: 12px 0" />

        <!-- Members section -->
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px">
          <span style="font-size: 14px; font-weight: 600; color: #374151">
            {{ $t("chat.members") }} ({{ selectedRoom.memberCount || 0 }})
          </span>
        </div>
        <div style="max-height: 300px; overflow-y: auto">
          <div
            v-for="member in selectedRoom.parentMembers || []"
            :key="`${member.loginId}-${member.parentType}`"
            style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; display: flex; align-items: center; gap: 10px"
          >
            <img
              :src="getAvatarUrl(member.photo, member.displayName || $t('chat.parent'))"
              :alt="$t('chat.parent')"
              style="width: 36px; height: 36px; border-radius: 50%; object-fit: cover; flex-shrink: 0"
            />
            <div style="flex: 1">
              <div style="font-weight: 600; font-size: 14px; color: #111">
                {{ member.displayName || $t("chat.parent") }}
              </div>
              <div style="font-size: 12px; color: #9ca3af; text-transform: capitalize">
                {{ member.parentType || "" }}
              </div>
            </div>
          </div>
          <div
            v-if="!selectedRoom.parentMembers || selectedRoom.parentMembers.length === 0"
            style="text-align: center; color: #9ca3af; padding: 20px 0; font-size: 13px"
          >
            {{ $t("chat.noParentsFound") }}
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
/* eslint-disable */

import { register } from "vue-advanced-chat";
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import ChatMessageVideoSlot from "@/views/chat/components/ChatMessageVideoSlot.vue";

import moment from "moment";

register();
export default {
  components: {
    ChatMessageVideoSlot,
  },
  computed: {
    ...mapState(["settings", "user"]),
    textMessages() {
      return {
        ROOMS_EMPTY: this.$t("chat.ROOMS_EMPTY"),
        ROOM_EMPTY: this.$t("chat.ROOM_EMPTY"),
        NEW_MESSAGES: this.$t("chat.NEW_MESSAGES"),
        MESSAGE_DELETED: this.$t("chat.MESSAGE_DELETED"),
        MESSAGES_EMPTY: this.$t("chat.MESSAGES_EMPTY"),
        CONVERSATION_STARTED: this.$t("chat.CONVERSATION_STARTED"),
        TYPE_MESSAGE: this.$t("chat.TYPE_MESSAGE"),
        SEARCH: this.$t("chat.SEARCH"),
        IS_ONLINE: this.$t("chat.IS_ONLINE"),
        LAST_SEEN: this.$t("chat.LAST_SEEN"),
      };
    },
    filteredRooms() {
      if (this.roomFilter === "group") {
        return this.rooms.filter((room) => room.roomType === "parent-group-supervision");
      }
      return this.rooms.filter((room) => room.roomType !== "parent-group-supervision");
    },
    messagesWithChatVideo() {
      return this.messages.filter((m) => {
        if (!m || m.system || m.deleted) return false;
        const files = m.files || [];
        return files.some((f) => this.fileNeedsCustomVideoPlayer(f));
      });
    },
    videoSlotRoomUsers() {
      return this.selectedRoom && this.selectedRoom.users
        ? this.selectedRoom.users
        : [];
    },
  },
  data() {
    return {
      loadedChat: true,
      teachers: [],
      roomsLoaded: false,
      currentUserId: "0",
      rooms: [],
      messages: [],
      messagesLoaded: false,
      selectedRoom: null,
      roomFilter: "individual",
      showGroupInfo: false,
      unseenMessages: [],
      messageActions: [],
    };
  },

  async created() {
    await apiClient
      .post("/teachers/filter", {
        query: { status: "active" },
      })
      .then((res) => (this.teachers = res.data));
  },

  methods: {
    formatFirstMiddleLast(p) {
      if (!p) return "";
      const parts = [p.firstName, p.middleName, p.lastName]
        .map((x) => (x != null && String(x).trim() ? String(x).trim() : ""))
        .filter(Boolean);
      return parts.join(" ");
    },
    async fetchAndUpdateRooms() {
      let unseenMessages = [];
      await apiClient.get("/pt-chat/teacher/unseenMessages").then((res) => {
        unseenMessages = res.data;
      });

      const newRooms = [];
      const existingRoomIds = new Set(this.rooms.map((room) => room.roomId));

      // get all parent chats
      await apiClient
        .get(`/pt-chat/teacher/${this.currentUserId}/parentChats`)
        .then((res) => {
          res.data.forEach((parent) => {
            // Determine which parent types to create rooms for
            const parentTypes = [];

            if (parent.parentType) {
              // If parentType is specified, only create room for that type
              parentTypes.push(parent.parentType);
            } else {
              // For legacy messages (parentType = null), create rooms for all parent types that have data
              if (parent.father && parent.father.firstName) {
                parentTypes.push("father");
              }
              if (parent.mother && parent.mother.firstName) {
                parentTypes.push("mother");
              }
              if (parent.guardian && parent.guardian.firstName) {
                parentTypes.push("guardian");
              }
            }

            // Create separate room for each parent type
            parentTypes.forEach((parentType) => {
              const parentData = parent[parentType];
              if (!parentData || !this.formatFirstMiddleLast(parentData)) {
                return; // Skip if no parent data for this type
              }

              const name = this.formatFirstMiddleLast(parentData);
              const compositeRoomId = `${parent._id}:${parentType}`;

              // Skip if room already exists
              if (existingRoomIds.has(compositeRoomId)) {
                return;
              }

              const photo = parentData.photo
                ? this.settings.resourcesUrl.replace(
                    "FILE_PATH",
                    parentData.photo
                  )
                : parent.photo
                ? this.settings.resourcesUrl.replace("FILE_PATH", parent.photo)
                : "-";

              // Create last message object only if it's relevant to this parent type
              let lastMessage = null;
              if (parent.lastMessage) {
                const messageParentType = parent.lastMessage.parentType;
                // Include message if it's legacy (no parentType) or matches current parent type
                if (!messageParentType || messageParentType === parentType) {
                  const messageContent = parent.lastMessage.deleted
                    ? `(${this.$t("chat.deleted")}) ${
                        parent.lastMessage.message
                      }`
                    : parent.lastMessage.message;

                  lastMessage = {
                    _id: parent.lastMessage._id,
                    content: messageContent,
                    senderId:
                      parent.lastMessage.sentBy == "parent"
                        ? parent.lastMessage.parentId
                        : this.currentUserId,
                    username: name,
                    saved: true,
                    timestamp: new Date(parent.lastMessage.createdAt)
                      .toString()
                      .substring(16, 21),
                    seen: parent.lastMessage.seen,
                    deleted: Boolean(parent.lastMessage.deleted),
                  };
                }
              }

              // Calculate unread count for this specific parent type
              const unseenCount =
                unseenMessages.find(
                  (p) => p._id === parent._id && p.parentType === parentType
                )?.count || 0;

              newRooms.push({
                roomId: compositeRoomId,
                parentId: parent._id,
                parentType: parentType,
                roomName: name,
                unreadCount: unseenCount,
                avatar: photo,
                lastMessage,
                users: [
                  {
                    _id: this.currentUserId,
                    username: this.$t("chat.teacher"),
                  },
                  {
                    _id: compositeRoomId,
                    username: name,
                  },
                ],
              });

              // Add to existing room IDs set to prevent duplicates within this iteration
              existingRoomIds.add(compositeRoomId);
            });
          });

          // Add new rooms to existing rooms
          this.rooms.push(...newRooms);

          // Remove any duplicate rooms (just in case)
          this.removeDuplicateRooms();

          // Set selected room if no room is currently selected
          if (this.rooms.length > 0 && !this.selectedRoom) {
            this.selectedRoom = this.rooms[0];
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.roomsLoaded = true;
        });

      // Add teacher-parent group chats (read-only supervision mode)
      await apiClient
        .get(`/teacherParentGroup/management/teachers/${this.currentUserId}/groups`)
        .then((res) => {
          const groups = res.data || [];
          const existingRoomIds = new Set(this.rooms.map((room) => room.roomId));
          const groupRooms = [];

          groups.forEach((group) => {
            const roomId = String(group._id);
            if (existingRoomIds.has(roomId)) return;

            const teacherSource =
              group?.teacher?.employee && typeof group.teacher.employee === "object"
                ? group.teacher.employee
                : group?.teacher || {};
            const teacherName =
              this.formatFirstMiddleLast(teacherSource) || this.$t("chat.teacher");
            const teacherPhoto = teacherSource.photo || null;

            const parentMembers = group.parentMembers || [];

            groupRooms.push({
              roomId,
              roomType: "parent-group-supervision",
              roomName: `[${this.$t("chat.group")}] ${group.name || this.$t("chat.group")}`,
              groupName: group.name || this.$t("chat.group"),
              unreadCount: 0,
              avatar: group.photo
                ? this.settings.resourcesUrl.replace("FILE_PATH", group.photo)
                : "media/avatars/blank.png",
              teacherName,
              teacherPhoto,
              parentMembers,
              memberCount: group.memberCount || parentMembers.length || 0,
              description: group.description || "",
              classRoom: group.classRoom || null,
              subject: group.subject || null,
              selectionMode: group.selectionMode || "manual",
              users: [
                {
                  _id: this.currentUserId,
                  username: this.$t("chat.manager"),
                },
                {
                  _id: group.teacher?._id || `teacher-${roomId}`,
                  username: teacherName,
                  avatar: teacherPhoto
                    ? this.settings.resourcesUrl.replace("FILE_PATH", teacherPhoto)
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        teacherName
                      )}&background=random&color=fff&size=40&rounded=true`,
                },
                ...parentMembers.map((member) => ({
                  _id: String(member.parentId || member.loginId),
                  username: member.displayName || this.$t("chat.parent"),
                  avatar: member.photo
                    ? this.settings.resourcesUrl.replace("FILE_PATH", member.photo)
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        member.displayName || this.$t("chat.parent")
                      )}&background=random&color=fff&size=40&rounded=true`,
                })),
              ],
            });
            existingRoomIds.add(roomId);
          });

          if (groupRooms.length > 0) {
            this.rooms.push(...groupRooms);
            this.removeDuplicateRooms();
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
    roomNewMessages(roomId, count) {
      const rooms = [...this.rooms];
      rooms.map((room) => {
        if (room.roomId === roomId) {
          room.unreadCount += count;
        }
        return room;
      });
      this.rooms = rooms;
    },
    fetchMessages({ room, options = {} }) {
      this.selectedRoom = room;
      const messages = [];

      this.messagesLoaded = false;

      room.unreadCount = 0;
      this.rooms.map((m) => {
        if (m.roomId == room.roomId) {
          m.unreadCount = 0;
        }
        return m;
      });

      if (room.roomType === "parent-group-supervision") {
        apiClient
          .get(`/teacherParentGroup/management/groups/${room.roomId}/messages`)
          .then((res) => {
            const rows = (res.data || []).slice().sort((a, b) => {
              return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            });

            rows.forEach((msg) => {
              const message_files = [];
              if (msg.files && msg.files.length > 0) {
                msg.files.forEach((file) => {
                  const splited = String(file).split(".");
                  const splitedName =
                    splited[splited.length - 2]?.split("--")[1] ||
                    this.$t("file");
                  const isAudio = this.isAudioFile(file);
                  message_files.push({
                    name: splitedName,
                    type: isAudio
                      ? this.getAudioMimeType(file)
                      : splited[splited.length - 1],
                    url: this.settings.resourcesUrl.replace("FILE_PATH", file),
                    preview: this.settings.resourcesUrl.replace("FILE_PATH", file),
                    audio: isAudio,
                    duration: isAudio ? 0 : undefined,
                  });
                });
              }

              const matchedParentMember =
                msg.senderType === "Parent"
                  ? (room.parentMembers || []).find(
                      (member) =>
                        (String(member.parentId) === String(msg.sender) ||
                          String(member.loginId) === String(msg.sender)) &&
                        (!msg.senderParentType ||
                          String(member.parentType) === String(msg.senderParentType))
                    )
                  : null;
              const senderName =
                msg.senderDisplayName ||
                (msg.senderType === "Parent"
                  ? matchedParentMember?.displayName || this.$t("chat.parent")
                  : room.teacherName || this.$t("chat.teacher"));
              const senderPhotoPath =
                msg.senderPhoto ||
                (msg.senderType === "Parent"
                  ? matchedParentMember?.photo || null
                  : room.teacherPhoto || null);

              const allGroupFiles = [
                ...message_files,
                ...this.mapVideosToFiles(msg.videos || []),
              ];

              messages.push({
                _id: msg._id,
                messageId: msg._id,
                content: msg.deleted
                  ? `(${this.$t("chat.deleted")}) ${msg.message || ""}`
                  : msg.message || "",
                senderId:
                  msg.senderType === "Parent"
                    ? String(matchedParentMember?.parentId || msg.sender)
                    : String(msg.sender),
                username: senderName,
                avatar: senderPhotoPath
                  ? this.settings.resourcesUrl.replace("FILE_PATH", senderPhotoPath)
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      senderName
                    )}&background=random&color=fff&size=40&rounded=true`,
                timestamp: new Date(msg.createdAt).toString().substring(16, 21),
                date: moment(msg.createdAt).format("DD/MM/YYYY"),
                files: allGroupFiles,
                seen: true,
                saved: true,
                deleted: Boolean(msg.deleted),
              });
            });

            setTimeout(() => {
              this.messagesLoaded = true;
              this.messages = messages;
            }, 100);
          })
          .catch((err) => {
            console.error(err);
          });
        return;
      }

      // Extract parentId and parentType from composite roomId
      const [parentId, parentType] = room.roomId.split(":");

      // get messages from db of selected room(parent id)
      const url = `/pt-chat/teacher/${this.currentUserId}/messages/${parentId}`;
      const params = parentType ? { parentType } : {};

      apiClient
        .get(url, { params })
        .then((res) => {
          res.data.map((msg) => {
            const message_files = [];
            if (msg.files && msg.files.length > 0) {
              msg.files.map((file) => {
                const splited = file.split(".");
                const splitedName = splited[splited.length - 2].split("--")[1];
                const isAudio = this.isAudioFile(file);
                message_files.push({
                  name: splitedName,
                  type: isAudio
                    ? this.getAudioMimeType(file)
                    : splited[splited.length - 1],
                  url: this.settings.resourcesUrl.replace("FILE_PATH", file),
                  preview: this.settings.resourcesUrl.replace(
                    "FILE_PATH",
                    file
                  ),
                  audio: isAudio,
                  duration: isAudio ? 0 : undefined,
                });
              });
            }

            const allPrivateFiles = [
              ...message_files,
              ...this.mapVideosToFiles(msg.videos || []),
            ];

            messages.push({
              _id: msg._id,
              messageId: msg._id,
              content: msg.deleted
                ? `(${this.$t("chat.deleted")}) ${msg.message}`
                : msg.message,
              senderId:
                msg.sentBy == "teacher" ? this.currentUserId : msg.parent,
              timestamp: new Date(msg.createdAt).toString().substring(16, 21),
              date: moment(msg.createdAt).format("DD/MM/YYYY"),
              files: allPrivateFiles,
              seen: msg.seen,
              saved: true,
              deleted: false,
              parentType: msg.parentType,
              isLegacy:
                typeof msg.parentType === "undefined" ||
                msg.parentType === null,
            });
          });

          // Insert a divider directly after the last legacy message (no parentType)
          let lastLegacyIndex = -1;
          for (let i = 0; i < res.data.length; i++) {
            const m = res.data[i];
            if (
              typeof m?.parentType === "undefined" ||
              m?.parentType === null
            ) {
              lastLegacyIndex = i;
            }
          }

          if (lastLegacyIndex >= 0) {
            const idx = Math.min(lastLegacyIndex, messages.length - 1);
            const dividerId = `system-legacy-divider-${room.roomId}`;
            if (!messages.find((m) => m?.messageId === dividerId)) {
              const dividerMessage = {
                _id: dividerId,
                messageId: dividerId,
                content: this.$t("chat.legacyMessageInfo"),
                system: true,
                saved: true,
                date: messages[idx]?.date,
              };
              messages.splice(lastLegacyIndex + 1, 0, dividerMessage);
            }
          }

          setTimeout(() => {
            this.messagesLoaded = true;
            this.messages = messages;
          }, 100);
        })
        .catch((err) => {
          console.error(err);
        });
    },

    openFile({ file }) {
      if (file.action == "download") window.open(file.file.url, "_blank");
    },
    handleRoomInfo(room) {
      if (room && room.roomType === "parent-group-supervision") {
        this.selectedRoom = room;
        this.showGroupInfo = true;
      }
    },

    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },

    async selectTeacher(val) {
      this.loadedChat = false;
      this.messages = [];
      this.rooms = [];
      this.selectedRoom = null;
      this.showGroupInfo = false;
      this.currentUserId = val;
      await this.fetchAndUpdateRooms();
      this.loadedChat = true;
    },

    removeDuplicateRooms() {
      const seen = new Set();
      this.rooms = this.rooms.filter((room) => {
        if (seen.has(room.roomId)) {
          return false; // Remove duplicate
        }
        seen.add(room.roomId);
        return true; // Keep first occurrence
      });
    },

    // Audio file detection function
    isAudioFile(url) {
      if (!url || typeof url !== "string") return false;
      const ext = url.split(".").pop()?.toLowerCase();
      return ["mp3", "aac"].includes(ext || "");
    },

    // Get MIME type from file extension
    getAudioMimeType(filename) {
      if (!filename || typeof filename !== "string") return "audio/mpeg";
      const ext = filename.split(".").pop()?.toLowerCase();
      const mimeTypes = {
        mp3: "audio/mpeg",
        aac: "audio/mp3", // Use audio/mp3 for better browser compatibility
      };
      return mimeTypes[ext || ""] || "audio/mpeg";
    },
    getAvatarUrl(photo, name) {
      if (photo) {
        return this.settings.resourcesUrl.replace("FILE_PATH", photo);
      }
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(
        name || this.$t("chat.user")
      )}&background=random&color=fff&size=40&rounded=true`;
    },

    handleVideoSlotDelete() {
      /* Supervision view is read-only; no server delete wired here. */
    },

    messageSlotKey(msg) {
      if (!msg) return "";
      const id = msg._id != null ? msg._id : msg.messageId;
      return id != null ? String(id) : "";
    },

    fileNeedsCustomVideoPlayer(file) {
      if (!file) return false;
      if (file.isChatVideo) return true;
      const t = (file.type || "").toLowerCase();
      const n = (file.name || "").toLowerCase();
      if (t.includes("video")) return true;
      return [".mp4", ".webm", ".mov", ".mkv"].some((ext) => n.endsWith(ext));
    },

    mapVideosToFiles(videos) {
      if (!videos || !videos.length) return [];
      return videos.map((rawVideo, i) => {
        const video =
          typeof rawVideo === "string" ? { originalKey: rawVideo } : rawVideo;
        const hlsUrl = video.hlsMasterKey
          ? this.settings.resourcesUrl.replace("FILE_PATH", video.hlsMasterKey)
          : null;
        const thumbnailUrl = video.thumbnailKey
          ? this.settings.resourcesUrl.replace("FILE_PATH", video.thumbnailKey)
          : null;
        const originalUrl = video.originalKey
          ? this.settings.resourcesUrl.replace("FILE_PATH", video.originalKey)
          : "";
        const label =
          video.status === "READY"
            ? `video-${i + 1}`
            : video.status === "FAILED"
            ? `video-${i + 1} (failed)`
            : `video-${i + 1} (processing…)`;
        const VIDEO_PLACEHOLDER = "/resources/images/video.png";
        const playableUrl = hlsUrl || originalUrl;
        const hasPlayableSource = Boolean(playableUrl);
        return {
          name: `${label}.mp4`,
          type: hasPlayableSource ? "video/mp4" : "png",
          extension: hasPlayableSource ? "mp4" : "png",
          url: hasPlayableSource ? playableUrl : VIDEO_PLACEHOLDER,
          preview: thumbnailUrl || (hasPlayableSource ? playableUrl : VIDEO_PLACEHOLDER),
          isChatVideo: true,
          videoData: {
            hlsUrl,
            thumbnailUrl,
            status: video.status || "PROCESSING",
            originalKey: video.originalKey,
            renditions: video.renditions || [],
            uploadProgress: 100,
          },
        };
      });
    },
  },
};
</script>
